import { styled } from '@mui/material/styles';
import { Box, Grid, Paper, Typography } from '@mui/material';
import React from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import _getTickerInfo from '../../../../../../pages/api/_getTickerInfo';
import * as send from '../../../../../../lib/analytics/send';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  marginBottom: '3px!important',
  '& .Item-Head': {
    fontSize: '.7rem',
    background: '#f2f2f2',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: ' 0 8px 0 8px',
    borderRadius: '6px 6px 0 0',
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 500,
      color: '#000',
      fontSize: '.875rem',
    }
  },
  '& .Item-Body': {
    padding: theme.spacing(1),
    color: '#13113c',
    gap: '4px'
  }
}));

const PriceTargets = ({ watchlist_metrics, ticker, isPremium, token }) => {
  const router = useRouter();
  const { data } = useSWR(
    ticker ? ["api/_getTickerInfo", ticker] : null,
    _getTickerInfo,
    {
      refreshInterval: 180000,
      keepPreviousData: true,
    }
  );

  if (!watchlist_metrics || watchlist_metrics.length === 0) return null;

  const { getTickerInfo } = data || {};
  const { realtime_stock_data } = getTickerInfo || {};
  const currentPrice = realtime_stock_data && realtime_stock_data.length > 0 ? realtime_stock_data[0].price : null;

  const getTargetColor = (targetValue) => {
    if (!currentPrice || !targetValue) return '#000';
    return parseFloat(targetValue) > currentPrice ? '#138615' : '#DB3353';
  };

  const getPercentChange = (targetValue) => {
    if (!currentPrice || !targetValue) return null;
    const change = ((parseFloat(targetValue) - currentPrice) / currentPrice) * 100;
    return change > 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`;
  };

  const handleClick = (e) => {
    if (isPremium) {
      e.preventDefault();
    } else if (!token) {
      router.push('/auth');
      send.uevent(e, 'auth-price-targets');
    } else {
      router.push('/pricing');
      send.uevent(e, 'price-targets-ad');
    }
  };

  const getDisplayText = () => {
    if (!token) return 'Login to View';
    if (token && !isPremium) return 'Check';
    return null;
  };

  return (
    <Grid size={{ xs: 12, sm: 12 }}>
      <Item
        component="div"
        className="about-company"
        sx={{
          mb: '8px',
          cursor: isPremium ? 'default' : 'pointer',
        }}
        onClick={handleClick}
      >
        <Box
          className="Item-Head"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 0.5,
            px: 0.5,
          }}
        >
          <Typography component="h3" className="option-title" sx={{ mr: 'auto', fontSize: '.875rem !important' }}>
            Price Targets
          </Typography>
        </Box>

        <Box className="Item-Body" sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {watchlist_metrics[0].TARGET && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '.875rem', fontWeight: 600, color: '#000' }}>
                Target 1Y
              </Typography>
              {isPremium ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '.875rem', fontWeight: 600, color: getTargetColor(watchlist_metrics[0].TARGET) }}>
                    ${watchlist_metrics[0].TARGET}
                  </Typography>
                  {getPercentChange(watchlist_metrics[0].TARGET) && (
                    <Typography variant="caption" sx={{ fontSize: '.75rem', color: getTargetColor(watchlist_metrics[0].TARGET) }}>
                      ({getPercentChange(watchlist_metrics[0].TARGET)})
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '.875rem',
                    fontWeight: 600,
                    color: '#174c86',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': !token ? { textDecoration: 'underline' } : {}
                  }}
                >
                  {getDisplayText()}
                  {!token && <KeyboardArrowRightRoundedIcon sx={{ fontSize: '1rem' }} />}
                </Typography>
              )}
            </Box>
          )}
          {watchlist_metrics[0].TARGET_6M && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '.875rem', fontWeight: 600, color: '#000' }}>
                Target 6M
              </Typography>
              {isPremium ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '.875rem', fontWeight: 600, color: getTargetColor(watchlist_metrics[0].TARGET_6M) }}>
                    ${watchlist_metrics[0].TARGET_6M}
                  </Typography>
                  {getPercentChange(watchlist_metrics[0].TARGET_6M) && (
                    <Typography variant="caption" sx={{ fontSize: '.75rem', color: getTargetColor(watchlist_metrics[0].TARGET_6M) }}>
                      ({getPercentChange(watchlist_metrics[0].TARGET_6M)})
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '.875rem',
                    fontWeight: 600,
                    color: '#174c86',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': !token ? { textDecoration: 'underline' } : {}
                  }}
                >
                  {getDisplayText()}
                  {!token && <KeyboardArrowRightRoundedIcon sx={{ fontSize: '1rem' }} />}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Item>
    </Grid>
  );
}

export default React.memo(PriceTargets);
